/**
 * BootstrapInterop - Replaces Bootstrap 5 JavaScript functionality.
 *
 * Handles:
 *   data-bs-toggle="modal"    + data-bs-target="#id"  → show/hide modals
 *   data-bs-dismiss="modal"                           → close parent modal
 *   data-bs-toggle="tab"      + data-bs-target="#id"  → switch tab panes
 *   data-bs-toggle="collapse" + data-bs-target="#id"  → toggle collapse
 *   data-bs-dismiss="alert"                           → remove parent alert
 *
 * Loads automatically via DOMContentLoaded + MutationObserver for
 * Blazor Server components that render after initial page load.
 */

class BootstrapInteropManager {
    private backdrop: HTMLElement | null = null;
    private activeModal: HTMLElement | null = null;
    private observer: MutationObserver | null = null;

    init(): void {
        this.bindAll(document);
        this.observeDom();
        document.addEventListener('keydown', this.handleEscape.bind(this));
    }

    /** Refresh bindings — call after dynamic content is added */
    refresh(): void {
        this.bindAll(document);
    }

    // ── BINDING ──────────────────────────────────────────────────────

    private bindAll(root: ParentNode): void {
        // Modal triggers
        root.querySelectorAll<HTMLElement>('[data-bs-toggle="modal"]').forEach(el => {
            if (el.dataset.bsBound) return;
            el.dataset.bsBound = '1';
            el.addEventListener('click', (e) => {
                e.preventDefault();
                const target = el.getAttribute('data-bs-target');
                if (target) this.showModal(document.querySelector(target));
            });
        });

        // Modal dismiss buttons
        root.querySelectorAll<HTMLElement>('[data-bs-dismiss="modal"]').forEach(el => {
            if (el.dataset.bsBound) return;
            el.dataset.bsBound = '1';
            el.addEventListener('click', () => {
                const modal = el.closest('.modal');
                if (modal) this.hideModal(modal as HTMLElement);
            });
        });

        // Tab triggers
        root.querySelectorAll<HTMLElement>('[data-bs-toggle="tab"]').forEach(el => {
            if (el.dataset.bsBound) return;
            el.dataset.bsBound = '1';
            el.addEventListener('click', (e) => {
                e.preventDefault();
                this.activateTab(el);
            });
        });

        // Collapse triggers
        root.querySelectorAll<HTMLElement>('[data-bs-toggle="collapse"]').forEach(el => {
            if (el.dataset.bsBound) return;
            el.dataset.bsBound = '1';
            el.addEventListener('click', () => {
                const target = el.getAttribute('data-bs-target');
                if (target) this.toggleCollapse(document.querySelector(target));
            });
        });

        // Dropdown triggers
        root.querySelectorAll<HTMLElement>('[data-bs-toggle="dropdown"]').forEach(el => {
            if (el.dataset.bsBound) return;
            el.dataset.bsBound = '1';
            el.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.toggleDropdown(el);
            });
        });

        // Alert dismiss
        root.querySelectorAll<HTMLElement>('[data-bs-dismiss="alert"]').forEach(el => {
            if (el.dataset.bsBound) return;
            el.dataset.bsBound = '1';
            el.addEventListener('click', () => {
                const alert = el.closest('.alert');
                if (alert) alert.remove();
            });
        });
    }

    // ── MODALS ───────────────────────────────────────────────────────

    showModal(modal: Element | null): void {
        if (!(modal instanceof HTMLElement)) return;

        // Create backdrop
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'modal-backdrop';
        this.backdrop.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:1040;';
        this.backdrop.addEventListener('click', () => this.hideModal(modal));
        document.body.appendChild(this.backdrop);

        // Show modal
        modal.style.display = 'block';
        modal.classList.add('show');
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        this.activeModal = modal;

        // Focus first focusable element
        requestAnimationFrame(() => {
            const focusable = modal.querySelector<HTMLElement>(
                'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
            );
            if (focusable) focusable.focus();
        });
    }

    hideModal(modal: HTMLElement): void {
        modal.classList.remove('show');
        modal.style.display = 'none';
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';

        if (this.backdrop) {
            this.backdrop.remove();
            this.backdrop = null;
        }
        this.activeModal = null;
    }

    // ── TABS ─────────────────────────────────────────────────────────

    private activateTab(clickedLink: HTMLElement): void {
        const target = clickedLink.getAttribute('data-bs-target');
        if (!target) return;

        // Deactivate sibling nav-links
        const navContainer = clickedLink.closest('.nav, .nav-tabs');
        if (navContainer) {
            navContainer.querySelectorAll('.nav-link').forEach(link => {
                link.classList.remove('active');
                link.setAttribute('aria-selected', 'false');
            });
        }

        // Activate clicked link
        clickedLink.classList.add('active');
        clickedLink.setAttribute('aria-selected', 'true');

        // Hide all sibling tab panes
        const pane = document.querySelector(target);
        if (!pane) return;
        const tabContent = pane.closest('.tab-content');
        if (tabContent) {
            tabContent.querySelectorAll('.tab-pane').forEach(p => {
                p.classList.remove('active', 'show');
            });
        }

        // Show target pane
        pane.classList.add('active', 'show');
    }

    // ── COLLAPSE ─────────────────────────────────────────────────────

    private toggleCollapse(target: Element | null): void {
        if (!(target instanceof HTMLElement)) return;
        target.classList.toggle('show');
    }

    // ── DROPDOWNS ─────────────────────────────────────────────────────

    private toggleDropdown(trigger: HTMLElement): void {
        const parent = trigger.closest('.dropdown') || trigger.parentElement;
        if (!parent) return;

        const menu = parent.querySelector('.dropdown-menu');
        if (!menu) return;

        const isOpen = menu.classList.contains('show');

        // Close all open dropdowns first
        this.closeAllDropdowns();

        if (!isOpen) {
            menu.classList.add('show');
            trigger.setAttribute('aria-expanded', 'true');

            // Close on outside click (one-time)
            const closeHandler = (e: Event) => {
                if (!parent.contains(e.target as Node)) {
                    menu.classList.remove('show');
                    trigger.setAttribute('aria-expanded', 'false');
                    document.removeEventListener('click', closeHandler);
                }
            };
            // Defer so this click doesn't immediately close it
            requestAnimationFrame(() => {
                document.addEventListener('click', closeHandler);
            });
        }
    }

    private closeAllDropdowns(): void {
        document.querySelectorAll('.dropdown-menu.show').forEach(menu => {
            menu.classList.remove('show');
        });
        document.querySelectorAll('[data-bs-toggle="dropdown"][aria-expanded="true"]').forEach(el => {
            el.setAttribute('aria-expanded', 'false');
        });
    }

    // ── KEYBOARD ─────────────────────────────────────────────────────

    private handleEscape(e: KeyboardEvent): void {
        if (e.key === 'Escape') {
            if (this.activeModal) {
                this.hideModal(this.activeModal);
            }
            this.closeAllDropdowns();
        }
    }

    // ── DOM OBSERVER (for Blazor Server late-rendered content) ────────

    private observeDom(): void {
        this.observer = new MutationObserver((mutations) => {
            for (let m = 0; m < mutations.length; m++) {
                const mutation = mutations[m];
                if (!mutation) continue;
                const nodes = mutation.addedNodes;
                for (let i = 0; i < nodes.length; i++) {
                    const node = nodes[i];
                    if (node instanceof HTMLElement) {
                        if (node.querySelector('[data-bs-toggle], [data-bs-dismiss]') ||
                            node.hasAttribute('data-bs-toggle') ||
                            node.hasAttribute('data-bs-dismiss')) {
                            this.bindAll(node.parentElement || document);
                            return; // One rebind per batch is enough
                        }
                    }
                }
            }
        });
        this.observer.observe(document.body, { childList: true, subtree: true });
    }
}

// ── GLOBAL SINGLETON ─────────────────────────────────────────────────

const instance = new BootstrapInteropManager();

// Auto-init on DOMContentLoaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => instance.init());
} else {
    instance.init();
}

// Augment global Window interface (script file — no module boundary)
interface Window {
    BootstrapInterop?: BootstrapInteropManager;
}

// Expose for programmatic use (e.g., Blazor calling showModal)
window.BootstrapInterop = instance;
